import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { MessageCircle, X } from 'lucide-react';

const MessageNotification = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showBadge, setShowBadge] = useState(false);
  const [isNewMessage, setIsNewMessage] = useState(false);
  const previousUnreadCount = useRef(0);

  const checkUnreadMessages = async () => {
    if (!user) return;

    try {
      const response = await apiService.getConversations();
      const totalUnread = response.conversations?.reduce(
        (total, conv) => total + (conv.unread_count || 0), 
        0
      ) || 0;
      
      // Detectar si hay mensajes nuevos
      if (totalUnread > previousUnreadCount.current && previousUnreadCount.current > 0) {
        setIsNewMessage(true);
        // Reproducir sonido de notificación
        playNotificationSound();
        // Auto-ocultar después de 5 segundos
        setTimeout(() => setIsNewMessage(false), 5000);
      }
      
      setUnreadCount(totalUnread);
      setShowBadge(totalUnread > 0);
      previousUnreadCount.current = totalUnread;
    } catch (error) {}
  };

  const playNotificationSound = () => {
    try {
      // Crear un audio simple (beep)
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
    }
  };

  useEffect(() => {
    if (user) {
      // Verificar inmediatamente
      checkUnreadMessages();
      
      // Verificar cada 30 segundos
      const interval = setInterval(checkUnreadMessages, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleClick = () => {
    // Ocultar la notificación inmediatamente al hacer click
    setShowBadge(false);
    // Navegar al chat
    navigate('/mensajes');
  };

  if (!user || !showBadge) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div 
        onClick={handleClick}
        className={`
          relative bg-pink-600 text-white rounded-full p-3 shadow-lg cursor-pointer 
          hover:bg-pink-700 transition-all duration-300 flex items-center space-x-2
          ${isNewMessage ? 'animate-pulse scale-110 shadow-2xl' : ''}
          hover:scale-105 active:scale-95
        `}
      >
        <MessageCircle className="h-6 w-6" />
        <span className="font-semibold">{unreadCount}</span>
        <span className="hidden sm:inline">mensaje{unreadCount !== 1 ? 's' : ''} nuevo{unreadCount !== 1 ? 's' : ''}</span>
        
        {/* Indicador de mensaje nuevo */}
        {isNewMessage && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-bounce"></div>
        )}
      </div>
    </div>
  );
};

export default MessageNotification;
